import { html, css, LitElement, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { Map, View } from 'ol'
import OSM from 'ol/source/OSM';
import TileLayer from 'ol/layer/Tile'
import olStylesText from 'ol/ol.css';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style'
import Point from 'ol/geom/Point'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { Modify, defaults as defaultInteractions } from 'ol/interaction'
import Feature from 'ol/Feature'
import { fromLonLat, toLonLat } from 'ol/proj'
import Overlay from 'ol/Overlay';
import { getWidth } from 'ol/extent';
import FullScreen from 'ol/control/FullScreen'
import { defaults as defaultControls } from 'ol/control/defaults';

export class NearbyMap extends LitElement {
    static styles = [
        unsafeCSS(olStylesText),
        css`
*, ::after, ::before {
    box-sizing: border-box;
}
.tooltip {
  position: absolute;
  background-color: white;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  pointer-events: none; /* Prevents the tooltip from flickering when the mouse enters it */
  display: none;
}
.ol-full-screen button {
  cursor: pointer;
}
:host { 
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
}
.ctr {
    width: 100%; 
    height: 100%; 
    background: #eee; 
}
.map { 
    width: 100%; 
    height: 100%; 
}
.ol-popup, .map, .ol-viewport {
    transition: none !important;
}
.ol-popup {
    position: absolute;
    background-color: white;
    padding: 15px;
    border-radius: 10px;
    border: 1px solid #fff;
    bottom: 0;   /* 🎯 Reset to 0: Let OpenLayers handle the vertical offset instead */
    left: -150px; /* Kept: Centers the 300px wide box exactly on the coordinate midpoint */
    font-weight: 300;
    font-size: 13px;
    filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
}

/* Update the arrow anchor triangle location to sit perfectly center */
.ol-popup::before, .ol-popup::after {
    border-top: 10px solid white;
    border-right: 10px solid transparent;
    border-left: 10px solid transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    bottom: -9px;
    left: 140px; /* Centered dynamically under a 300px wide layout box */
}
    .ol-popup-closer { text-decoration: none; position: absolute; top: 2px; right: 8px; color: #333; }
.title {
    font-weight: bold;
    margin-top: 8px;
    margin-bottom: 8px;
}
a {
    color: #333;
    text-decoration: none;
}
.multiple .title { 
    margin-top: 8px!important;
    margin-bottom: 8px!important;
}
.medium, .address {
    margin-bottom: 10px;
}
.infowindow {
    font-size: 12px;
    max-height: 250px;
    overflow-y: auto;
    word-break: break-all;
}
.infowindow a {
    text-decoration: underline;
}
.infowindow.multiple .item:not(:last-child) {
    margin-bottom: 30px;
}
.link {
    margin-top: 6px;
}
img {
    display: block;
}
        `
    ];
    constructor() {
        super()
        this.isLoading = true;
        console.log("Hello World!")
    }

    static properties = {
        isLoading: { type: Boolean }
    };

    markerSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="red" d="M256 0C160 0 82 78 82 174c0 101 80 164 123 239 42 75 42 102 51 102s9-27 51-99c43-75 123-138 123-239C430 78 352 0 256 0zm0 240c-37 0-67-30-67-67 0-37 30-67 67-67s67 30 67 67c0 37-30 67-37 67z"/><circle cx="256" cy="174" r="67" fill="rgba(0,0,0,0.01)"/></svg>`;
    markerSVGUrl = 'data:image/svg+xml;base64,' + btoa(this.markerSVG);
    markerStyle = new Style({
        image: new Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: this.markerSVGUrl,
            scale: 0.05
        }),
    });
    markerLayer = new VectorLayer({
        source: new VectorSource(),
        renderOptions: {
            willReadFrequently: true
        }
    })

    setupInfoWindow() {
        const container = this.shadowRoot.getElementById('popup');
        this.infocontent = this.shadowRoot.getElementById('popup-content');
        const closer = this.shadowRoot.getElementById('popup-closer');

        this.infowindow = new Overlay({
            element: container,
            offset: [0, -45],
            positioning: 'bottom-center', // 🎯 Crucial: Forces the arrow to sit directly on the marker coordinate
            stopEvent: false,             // Allows map interactions to flow normally 
            autoPan: {
                animation: {
                    duration: 250,
                },
            },
        });
        this.map.addOverlay(this.infowindow);

        closer.onclick = (e) => {
            e.preventDefault();
            this.infowindow.setPosition(undefined);
            return false;
        };
    }

    async loadMarkers() {
        try {
            const response = await fetch('/api/map/places');
            if (!response.ok) throw new Error('Network response was not ok');

            const locations = await response.json();

            console.log(locations)
            const source = this.markerLayer.getSource()

            // Map data objects to OpenLayers geometric elements
            const features = locations.map(place => {
                const marker = new Feature({
                    geometry: new Point(fromLonLat([place.longitude, place.latitude])),
                    placeData: place // Keep metadata linked for clicks
                });
                const style = place.icon ? new Style({
                    image: new Icon({
                        anchor: [0.5, 1], // Pin point alignment
                        src: place.icon,
                        scale: 1
                    })
                }) : this.markerStyle;

                marker.setStyle(style)
                source.addFeature(marker)
                return marker;
            });

            //const vectorSource = new VectorSource({ features: features });
            //const vectorLayer = new VectorLayer({ source: vectorSource });

            this.map.addLayer(this.markerLayer);

        } catch (error) {
            console.error("Failed to fetch map markers from endpoint:", error);
        }
    } 
    closePopup() {
        this.infowindow.setPosition(undefined);
    }

    getInfoWindowContent(features) {
        let result = ``
        features.forEach(feature => {
            const placeData = feature.get("placeData")
            if (placeData) {
                console.log(placeData)
                const imgHtml = placeData.imageUrl ? `<img style="width:190px;height:120px;object-fit: cover;" src="${placeData.imageUrl}" alt="${placeData.name}">` : ''
                const address = placeData.address ? `<div>${placeData.address}</div>` : ``
                const suburb = placeData.suburb && placeData.suburb != placeData.address ? `<div>${placeData.suburb}</div>` : ``
                const url = placeData.url
                const urlName = url ? url.replace(/https:\/\/www\./, '').replace(/https:\/\//, '').replace(/http:\/\/www\./, '').replace(/http:\/\//, '').replace(/\/$/, "") : ''
                const urlEl = url ? `<div class="link"><a href="${url}" target="_blank">${urlName}</a></div>` : `` 

                result += `
                <div class="item">
                    <div>${imgHtml}</div>
                    <div class="title">${placeData.name}</div>
                    ${address}
                    ${suburb}
                    ${urlEl}
                </div>
                `
            }
        })
        const multiple = features.length > 1 ? ` multiple` : ""
        result = `<div class="infowindow${multiple}">${result}</div>`
        return result
    }
    handleMapClick(source, feature) {
        if (feature) {
            const coordinates = feature.getGeometry().getCoordinates();

            // get any artists with the same coordinates (for multiple artists at the same location)
            const matchingFeatures = source.getFeaturesAtCoordinate(coordinates);
            //console.log("Matching features at coordinates:", matchingFeatures)

            this.infocontent.innerHTML = this.getInfoWindowContent(matchingFeatures)
            this.infocontent.querySelectorAll(".title a").forEach(link => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    if (document.fullscreenElement) {
                        document.exitFullscreen()
                            .then(() => console.log("Exited full screen"))
                            .catch((err) => console.error(err));
                    }
                });
            });

            //this.infowindow.setPosition(undefined);
            this.infowindow.setPosition(coordinates);
            //this.infowindow.panIntoView();
        } else {
            this.closePopup()
        }
    }
    async firstUpdated() {
        const mapContainer = this.shadowRoot.querySelector('.map');
        console.log("Parent", this.parentElement)
        const lat = this.parentElement.dataset.lat
        const lng = this.parentElement.dataset.lng
        const zoom = this.parentElement.dataset.zoom
        console.log(lat, lng, zoom)
        this.map = new Map({
            target: mapContainer,
            interactions: defaultInteractions({
                mouseWheelZoom: false
            }),
            controls: defaultControls().extend([
                new FullScreen()
            ]),
            layers: [
                new TileLayer({
                    source: new OSM(),
                }),
            ],
            view: new View({
                center: fromLonLat([lng, lat]),
                zoom: zoom,
            }),
            rendererOptions: {
                willReadFrequently: true
            }
        });
        this.setupInfoWindow()
        await this.loadMarkers();
        this.map.on('singleclick', (evt) => {
            const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);
            const source = this.markerLayer.getSource()
            this.handleMapClick(source, feature);
        })
        this._handleEsc = (event) => {
            if (event.key === 'Escape') {
                this.closePopup();
            }
        };

        let pmTimer = null;

        this.map.on('pointermove', (evt) => {
            if (pmTimer) return;

            pmTimer = setTimeout(() => {
                //const hit = this.map.hasFeatureAtPixel(evt.pixel);
                const tooltipElement = this.shadowRoot.getElementById('tooltip');
                const feature = this.map.forEachFeatureAtPixel(evt.pixel, function (feature) {
                    return feature;
                });
                console.log(feature)
                if (feature && feature.get("placeData")) {
                    // Show tooltip and set text
                    tooltipElement.innerHTML = feature.get("placeData").name;
                    tooltipElement.style.display = 'block';
                    tooltipElement.style.fontSize = "12px";
                    // Position the tooltip near the mouse
                    tooltipElement.style.left = (evt.pixel[0] + 10) + 'px';
                    tooltipElement.style.top = (evt.pixel[1] + 10) + 'px';
                } else {
                    // Hide tooltip if not hovering
                    tooltipElement.style.display = 'none';
                }

                const hit = this.map.hasFeatureAtPixel(evt.pixel, {
                    layerFilter: (layer) => {
                        // Return true ONLY for your interactive vector layer
                        // All other layers bypass the heavy hit-detection engine completely
                        return layer === this.markerLayer;
                    }
                });

                const mapTarget = this.map.getTargetElement();
                if (mapTarget) {
                    mapTarget.style.cursor = hit ? 'pointer' : '';
                }

                pmTimer = null;
            }, 100); // 100ms reduces the readback load to a comfortable 10fps
        });

        this.isLoading = false;

        await this.updateComplete;
        this.map.updateSize();

        document.addEventListener('keydown', this._handleEsc);

    }


    render() {
        return html`
        <div class="ctr">
            <div class="map"></div>
            ${this.isLoading ? html`<div class="spinner">🌀 Loading Map...</div>` : ''}
            <div id="tooltip" class="tooltip"></div>
            <div id="popup" class="ol-popup">
                <a href="#" id="popup-closer" class="ol-popup-closer">✖</a>
                <div id="popup-content"></div>
            </div>
        </div>
    `;
    }

}
customElements.define('nearby-map', NearbyMap);
